import { AssetUploadForm } from '@/components/admin/asset-upload';
import { ASSET_ACCEPT } from '@/lib/ambiance-labels';
import { useId } from 'react';
import { MEDIATHEQUE_UPLOAD_DIALOG_TESTID } from './contracts';

/**
 * « Ajouter un fichier » (issue #121): a small form in an overlay, at the
 * régime of `personas/persona-tone-dialog.tsx` and NOT `versioned-dialog.tsx`
 * - that shell puts the focus on « Annuler » because its three boxes SHOW AN
 * EFFECT before it happens, where this one's first gesture is picking a file.
 * A native `<dialog open>` kept in flow by React state, Escape wired by hand
 * because `open` alone does not wire it.
 *
 * `AssetUploadForm` is mounted as-is - it already carries the uncontrolled
 * fields, the covered file input and the French refusal line; this file only
 * gives it a title, a backdrop and the wide `ASSET_ACCEPT.all` accept list,
 * since the library takes both natures.
 */

export interface MediathequeUploadDialogProps {
  busy: boolean;
  /** The French of the last refusal, worded from its code; null when none. */
  refusal: string | null;
  onUpload: (file: File, license: string, tags: string) => void;
  onCancel: () => void;
}

export function MediathequeUploadDialog({
  busy,
  refusal,
  onUpload,
  onCancel,
}: MediathequeUploadDialogProps) {
  const titleId = useId();
  return (
    <div
      data-testid={MEDIATHEQUE_UPLOAD_DIALOG_TESTID}
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-[var(--backdrop)] p-5 py-10"
    >
      <dialog
        open
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
        className="relative m-0 w-full max-w-2xl space-y-5 rounded-md border-2 border-gray-200 bg-white p-6 shadow-lg"
      >
        <div className="space-y-1">
          <h2 id={titleId} className="font-semibold text-bleu-nuit text-h4">
            Ajouter un fichier
          </h2>
          <p className="text-gray-700 text-sm">
            Une image ou un son, pour la médiathèque : la licence et les mots-clés restent
            facultatifs.
          </p>
        </div>

        <AssetUploadForm
          busy={busy}
          accept={ASSET_ACCEPT.all}
          refusal={refusal}
          onUpload={onUpload}
          onCancel={onCancel}
        />
      </dialog>
    </div>
  );
}
